/**
 * useOfficeDashboard Hook
 *
 * Aggregates data from multiple hooks for the Office Dashboard hub.
 * Combines deals, jobs, SOS alerts, faults, and activity feeds
 * into a single unified interface for the dashboard page.
 *
 * Field activity events are persisted to Firestore (field_activity_log)
 * so they remain visible for the full day and are linked to jobs/deals.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useJobs } from './useJobs';
import { useDeals } from './useDeals';
import { useFaults } from './useFaults';
import { useSOSAlerts } from './useSOSAlerts';
import { useFlagAlerts } from './useFlagAlerts';
import { useTimeRequestAlerts } from './useTimeRequestAlerts';
import { useDashboardFeed } from './useDashboardFeed';
import { useTenantId } from './useTenantId';
import { useTeam } from './useTeam';
import { DealStage } from '@/types/theo-workflow.types';
import { migrateFaultStage } from '@/types/fault.types';
import { evaluateNeedsAttention } from '@/services/deal-workflow.service';
import {
  persistFieldActivity,
  subscribeFieldActivityByDate,
} from '@/services/field-activity.service';
import type { FieldActivityLog } from '@/services/field-activity.service';
import { composeSiteAddress } from '@/utils/address';

export interface FieldActivityItem {
  id: string;
  type: 'on_site' | 'completed' | 'sos' | 'flagged' | 'request_more_time' | 'scheduled';
  title: string;
  subtitle: string;
  timestamp: string;
  severity?: 'red' | 'amber' | 'orange' | 'green' | 'blue';
  // Metadata for persistence and record-keeping
  jobId?: string;
  dealId?: string;
  techId?: string;
  techName?: string;
  customerName?: string;
  address?: string;
  jobType?: string;
  // Alert collection reference (for office actions)
  alertId?: string;
  acknowledged?: boolean;
}

/** Format a Date to YYYY-MM-DD using local timezone */
function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface AttentionItem {
  id: string;
  title: string;
  subtitle: string;
  reasons: string[];
  severity: 'red' | 'yellow';
  type: 'deal' | 'job';
}

export function useOfficeDashboard() {
  const { jobs, loading: jobsLoading } = useJobs();
  const { deals, loading: dealsLoading } = useDeals();
  const { faults, loading: faultsLoading } = useFaults();
  const tenantId = useTenantId();
  const { alerts: sosAlerts, loading: sosLoading } = useSOSAlerts(tenantId);
  const { alerts: flagAlerts } = useFlagAlerts(tenantId);
  const { alerts: timeRequestAlerts } = useTimeRequestAlerts(tenantId);
  const { feedItems, highPriorityItems, loading: feedLoading } = useDashboardFeed();
  const { team } = useTeam();

  // Persisted field activity from Firestore (full day history)
  const [persistedActivity, setPersistedActivity] = useState<FieldActivityItem[]>([]);

  // Subscribe to today's persisted field activity
  useEffect(() => {
    const today = toLocalDateStr(new Date());
    const unsubscribe = subscribeFieldActivityByDate(today, (items: FieldActivityLog[]) => {
      setPersistedActivity(
        items.map((item) => ({
          id: item.eventId,
          type: item.type as FieldActivityItem['type'],
          title: item.title,
          subtitle: item.subtitle,
          timestamp: item.timestamp,
          severity: item.severity as FieldActivityItem['severity'],
          jobId: item.jobId,
          dealId: item.dealId,
          techId: item.techId,
          techName: item.techName,
          customerName: item.customerName,
          address: item.address,
          jobType: item.jobType,
        }))
      );
    });
    return unsubscribe;
  }, []);

  // Resolve tech ID to display name from team data
  const getTechName = useCallback(
    (techId: string | undefined): string => {
      if (!techId) return 'Tech';
      const member = team.find((t) => t.id === techId);
      return member?.name || techId;
    },
    [team]
  );

  // === Stats ===
  const stats = useMemo(() => {
    const activeDeals = deals.filter((d) =>
      ![DealStage.COMPLETED, DealStage.LOST].includes(d.stage)
    );
    const wonDeals = deals.filter((d) =>
      [DealStage.WON, DealStage.PROVISIONING, DealStage.SCHEDULING, DealStage.REVIEW, DealStage.BILLING].includes(d.stage)
    );
    const attentionDeals = deals.filter(
      (d) => ![DealStage.COMPLETED, DealStage.LOST].includes(d.stage) &&
        (d.needsAttention || evaluateNeedsAttention(d).length > 0)
    );

    const today = toLocalDateStr(new Date());
    const todayJobs = jobs.filter((j) => j.date === today);
    const activeJobs = todayJobs.filter((j) =>
      j.status === 'In Progress' || j.status === 'Scheduled'
    );

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = toLocalDateStr(tomorrow);
    const tomorrowJobs = jobs.filter((j) => j.date === tomorrowStr);

    // This week's jobs
    // Australian weeks run Monday→Sunday. getDay() is 0=Sun..6=Sat, so a
    // bare `- getDay() + 1` jumps to NEXT Monday on a Sunday; (+6)%7 re-bases
    // to 0=Mon..6=Sun so Sunday correctly stays in the week it ends.
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - ((startOfWeek.getDay() + 6) % 7));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6); // Sunday
    const weekStart = toLocalDateStr(startOfWeek);
    const weekEnd = toLocalDateStr(endOfWeek);
    const thisWeekJobs = jobs.filter((j) => j.date >= weekStart && j.date <= weekEnd);

    const openFaults = faults.filter((f) => !f.archived);
    const criticalFaults = faults.filter((f) => f.severity === 'critical');

    const pipelineValue = activeDeals.reduce((sum, d) => sum + (d.value ?? 0), 0);

    return {
      activeDeals: activeDeals.length,
      wonDeals: wonDeals.length,
      attentionDeals: attentionDeals.length,
      todayJobs: todayJobs.length,
      activeJobs: activeJobs.length,
      tomorrowJobs: tomorrowJobs.length,
      thisWeekJobs: thisWeekJobs.length,
      sosAlerts: sosAlerts.length,
      flaggedJobs: jobs.filter((j) => j.flagged).length,
      timeRequests: timeRequestAlerts.length,
      openFaults: openFaults.length,
      criticalFaults: criticalFaults.length,
      pipelineValue,
    };
  }, [deals, jobs, faults, sosAlerts, timeRequestAlerts]);

  // === Needs Attention Items ===
  const attentionItems = useMemo<AttentionItem[]>(() => {
    const items: AttentionItem[] = [];

    // Deals needing attention (exclude terminal stages — LOST/COMPLETED don't need action)
    deals
      .filter((d) => d.stage !== DealStage.LOST && d.stage !== DealStage.COMPLETED)
      .forEach((deal) => {
        const checks = evaluateNeedsAttention(deal);
        if (checks.length > 0) {
          const isRed = checks.some((c) => c.severity === 'red');
          items.push({
            id: deal.id || '',
            title: deal.title || 'Untitled Deal',
            subtitle: deal.customerName || deal.contactName || '',
            reasons: checks.map((c) => c.reason),
            severity: isRed ? 'red' : 'yellow',
            type: 'deal',
          });
        }
      });

    // Jobs needing scheduling (deals in scheduling stage with unscheduled jobs)
    const schedulingDeals = deals.filter((d) => d.stage === DealStage.SCHEDULING);
    const unscheduledDeals = schedulingDeals.filter((deal) => {
      const dealJobs = jobs.filter((j) => j.dealId === deal.id);
      // If no linked jobs exist yet, it still needs scheduling
      if (dealJobs.length === 0) return true;
      // If any linked job has no date, it still needs scheduling
      return dealJobs.some((j) => !j.date);
    });
    if (unscheduledDeals.length > 0) {
      items.push({
        id: 'scheduling-needed',
        title: `${unscheduledDeals.length} deal${unscheduledDeals.length > 1 ? 's' : ''} need scheduling`,
        subtitle: unscheduledDeals.map((d) => d.title).slice(0, 3).join(', '),
        reasons: ['Ready for job scheduling'],
        severity: 'yellow',
        type: 'deal',
      });
    }

    // Sort: red first, then yellow
    items.sort((a, b) => (a.severity === 'red' ? -1 : 1) - (b.severity === 'red' ? -1 : 1));

    return items;
  }, [deals, jobs]);

  // === Live Field Activity (computed from current job/SOS state) ===
  const liveFieldActivity = useMemo<FieldActivityItem[]>(() => {
    const items: FieldActivityItem[] = [];

    // SOS alerts (highest priority)
    sosAlerts.forEach((alert) => {
      items.push({
        id: `sos-${alert.id}`,
        type: 'sos',
        title: `SOS: ${alert.techName || 'Technician'}`,
        subtitle: alert.reason || 'Emergency assistance needed',
        timestamp: alert.triggeredAt || new Date().toISOString(),
        severity: 'red',
        jobId: alert.jobId,
        techId: alert.techId,
        techName: alert.techName,
        customerName: alert.customerName,
        address: alert.address,
        alertId: alert.id,
        acknowledged: alert.acknowledged,
      });
    });

    // Flagged jobs — cross-reference with flag_alerts for alertId
    jobs.filter((j) => j.flagged && j.status !== 'Completed').forEach((job) => {
      const matchingAlert = flagAlerts.find((a) => a.jobId === job.id);
      items.push({
        id: `flag-${job.id}`,
        type: 'flagged',
        title: `Flagged: ${job.flaggedByName || job.client}`,
        subtitle: job.flagReason || 'Issue reported on-site',
        timestamp: job.flaggedAt || job.updatedAt || job.createdAt || new Date().toISOString(),
        severity: 'amber',
        jobId: job.id,
        dealId: job.dealId,
        techId: job.flaggedBy || job.techId,
        techName: job.flaggedByName || getTechName(job.techId),
        customerName: job.client,
        address: composeSiteAddress(job),
        alertId: matchingAlert?.id,
        acknowledged: job.flagAcknowledged,
      });
    });

    // Request more time — prefer alert collection (richer data), fall back to job fields
    const jobsWithTimeAlerts = new Set<string>();
    timeRequestAlerts.forEach((alert) => {
      jobsWithTimeAlerts.add(alert.jobId);
      const extraLabel = alert.extraMinutes
        ? ` (+${alert.extraMinutes >= 60 ? `${alert.extraMinutes / 60}h` : `${alert.extraMinutes}m`})`
        : '';
      items.push({
        id: `rmt-${alert.id}`,
        type: 'request_more_time',
        title: `More Time: ${alert.techName || 'Technician'}${extraLabel}`,
        subtitle: alert.reason || 'Additional time requested',
        timestamp: alert.requestedAt || new Date().toISOString(),
        severity: 'orange',
        jobId: alert.jobId,
        techId: alert.techId,
        techName: alert.techName,
        customerName: alert.customerName,
        address: alert.address,
        alertId: alert.id,
        acknowledged: alert.acknowledged,
      });
    });
    // Fallback: jobs with requestMoreTime flag but no alert doc (backward compat)
    jobs.filter((j) => j.requestMoreTime && j.status !== 'Completed' && j.id && !jobsWithTimeAlerts.has(j.id)).forEach((job) => {
      const extraLabel = job.requestMoreTimeMinutes
        ? ` (+${job.requestMoreTimeMinutes >= 60 ? `${job.requestMoreTimeMinutes / 60}h` : `${job.requestMoreTimeMinutes}m`})`
        : '';
      items.push({
        id: `rmt-${job.id}`,
        type: 'request_more_time',
        title: `More Time: ${job.requestMoreTimeByName || job.client}${extraLabel}`,
        subtitle: job.requestMoreTimeReason || 'Additional time requested',
        timestamp: job.requestMoreTimeAt || job.updatedAt || job.createdAt || new Date().toISOString(),
        severity: 'orange',
        jobId: job.id,
        dealId: job.dealId,
        techId: job.requestMoreTimeBy || job.techId,
        techName: job.requestMoreTimeByName || getTechName(job.techId),
        customerName: job.client,
        address: composeSiteAddress(job),
      });
    });

    // In-progress jobs (techs on-site)
    const today = toLocalDateStr(new Date());
    jobs
      .filter((j) => j.status === 'In Progress' && j.date === today)
      .forEach((job) => {
        const techName = getTechName(job.techId);
        items.push({
          id: `onsite-${job.id}`,
          type: 'on_site',
          title: `${techName} on-site`,
          subtitle: `${job.client} — ${composeSiteAddress(job)}`,
          timestamp: job.startedAt || job.updatedAt || new Date().toISOString(),
          severity: 'green',
          jobId: job.id,
          dealId: job.dealId,
          techId: job.techId,
          techName,
          customerName: job.client,
          address: composeSiteAddress(job),
          jobType: job.type,
        });
      });

    // All completed jobs today (no limit - full day history)
    jobs
      .filter((j) => j.status === 'Completed' && j.date === today)
      .forEach((job) => {
        const resolved = job.techId ? getTechName(job.techId) : null;
        const techName = (resolved && resolved !== 'Tech') ? resolved : (job.completedBy ? getTechName(job.completedBy) : 'Tech');
        const durationLabel = job.actualDuration
          ? job.actualDuration >= 60
            ? `${Math.floor(job.actualDuration / 60)}h ${job.actualDuration % 60 ? `${job.actualDuration % 60}m` : ''}`
            : `${job.actualDuration}m`
          : '';
        items.push({
          id: `done-${job.id}`,
          type: 'completed',
          title: `${techName} completed ${job.type || 'Job'}`,
          subtitle: `${job.client} — ${composeSiteAddress(job)}${durationLabel ? ` · ${durationLabel.trim()}` : ''}`,
          timestamp: job.completedAt || job.updatedAt || new Date().toISOString(),
          severity: 'blue',
          jobId: job.id,
          dealId: job.dealId,
          techId: job.completedBy || job.techId,
          techName,
          customerName: job.client,
          address: composeSiteAddress(job),
          jobType: job.type,
        });
      });

    // Sort by timestamp desc
    items.sort((a, b) => b.timestamp.localeCompare(a.timestamp));

    return items;
  }, [jobs, sosAlerts, flagAlerts, timeRequestAlerts, getTechName]);

  // === Persist live events to Firestore ===
  const persistedKeysRef = useRef(new Set<string>());
  useEffect(() => {
    if (!tenantId) return;
    const today = toLocalDateStr(new Date());

    liveFieldActivity.forEach((item) => {
      // Track by id + title so updated names get re-persisted
      const key = `${item.id}::${item.title}`;
      if (persistedKeysRef.current.has(key)) return;
      persistedKeysRef.current.add(key);

      const docId = `${today}_${item.id}`;
      persistFieldActivity({
        id: docId,
        eventId: item.id,
        type: item.type as FieldActivityLog['type'],
        title: item.title,
        subtitle: item.subtitle,
        timestamp: item.timestamp,
        severity: (item.severity || 'blue') as FieldActivityLog['severity'],
        date: today,
        jobId: item.jobId,
        dealId: item.dealId,
        techId: item.techId,
        techName: item.techName,
        customerName: item.customerName,
        address: item.address,
        jobType: item.jobType,
        tenantId,
      }).catch(console.error);
    });
  }, [liveFieldActivity, tenantId]);

  // === Merge persisted + live field activity ===
  // Persisted provides historical items (e.g. "on-site" after job completes)
  // Live overwrites persisted for current items (fresher names/data)
  const fieldActivity = useMemo<FieldActivityItem[]>(() => {
    const map = new Map<string, FieldActivityItem>();
    // Add persisted first (historical)
    persistedActivity.forEach((item) => map.set(item.id, item));
    // Live overwrites persisted (current state has freshest data)
    liveFieldActivity.forEach((item) => map.set(item.id, item));
    const merged = Array.from(map.values());
    merged.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    return merged;
  }, [persistedActivity, liveFieldActivity]);

  // === Upcoming summary ===
  const upcoming = useMemo(() => {
    const awaitingProvisioning = deals.filter((d) => d.stage === DealStage.PROVISIONING).length;
    const pendingQuotes = deals.filter(
      (d) => d.stage === DealStage.QUOTING || d.stage === DealStage.PROPOSAL_SENT
    ).length;

    return {
      tomorrowJobs: stats.tomorrowJobs,
      thisWeekJobs: stats.thisWeekJobs,
      awaitingProvisioning,
      pendingQuotes,
    };
  }, [deals, stats]);

  // === Action Board: cross-business items that need doing ===
  const actionBoard = useMemo(() => {
    const active = deals.filter((d) => d.stage !== DealStage.COMPLETED && d.stage !== DealStage.LOST);

    // Sales actions
    const dealsAtIntake = active.filter((d) => d.stage === DealStage.INTAKE).length;
    const dealsNeedingQuote = active.filter(
      (d) => d.stage === DealStage.QUOTING || d.stage === DealStage.PROPOSAL_SENT
    ).length;
    const dealsWon = active.filter((d) => d.stage === DealStage.WON).length;

    // Provisioning actions
    const dealsNeedingProvisioning = active.filter((d) => d.stage === DealStage.PROVISIONING).length;

    // Scheduling actions — only count jobs actually waiting to be scheduled
    const unscheduledJobs = jobs.filter((j) => !j.date && j.status !== 'Completed').length;

    // Return visits — surfaces orphan follow-up jobs (no deal/fault link) that
    // would otherwise just sit in the unscheduled bucket with no context.
    // Ready: follow-up jobs auto-created by the immediate-reschedule path that
    // are still unscheduled. Blocked: original jobs the tech flagged with a
    // blocked reason (waiting on parts, etc.) — no follow-up was created so the
    // original is the only surface.
    const returnVisitsReady = jobs.filter(
      (j) => j.lastAction === 'return_visit_created' && !j.date && j.status !== 'Completed',
    ).length;
    const returnVisitsBlocked = jobs.filter(
      (j) => j.needsReturnVisit === true && (j as { returnVisitStatus?: string }).returnVisitStatus === 'blocked',
    ).length;

    // Review & billing
    const dealsNeedingReview = active.filter((d) => d.stage === DealStage.REVIEW).length;
    const dealsNeedingBilling = active.filter((d) => d.stage === DealStage.BILLING).length;

    // Faults — route raw doc stages through migrateFaultStage so legacy
    // stage strings still bucket correctly (they used to fall out silently).
    const openFaults = faults.filter((f) => !f.archived);
    const faultsAtIntake = openFaults.filter(
      (f) => migrateFaultStage(f.stage) === '01-Triage'
    ).length;
    const faultsNeedingAction = openFaults.filter(
      (f) => migrateFaultStage(f.stage) === '02-Remote'
    ).length;
    const faultsAwaitingDeploy = openFaults.filter(
      (f) => migrateFaultStage(f.stage) === '03-Deploy'
    ).length;
    const faultsFollowUp = openFaults.filter(
      (f) => migrateFaultStage(f.stage) === '04-Review'
    ).length;

    return {
      dealsAtIntake,
      dealsNeedingQuote,
      dealsWon,
      dealsNeedingProvisioning,
      unscheduledJobs,
      returnVisitsReady,
      returnVisitsBlocked,
      dealsNeedingReview,
      dealsNeedingBilling,
      faultsAtIntake,
      faultsNeedingAction,
      faultsAwaitingDeploy,
      faultsFollowUp,
    };
  }, [deals, jobs, faults]);

  const loading = jobsLoading || dealsLoading || faultsLoading || sosLoading || feedLoading;

  return {
    stats,
    attentionItems,
    fieldActivity,
    upcoming,
    actionBoard,
    feedItems,
    highPriorityItems,
    loading,
  };
}
